/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();

import { ChevronLeft, ChevronRight } from 'lucide-react'
import React, { useEffect, useRef, useState } from 'react'

import { ActivityCardContent } from '@/components/quality-explorer/carousel/ActivityCardContent'
import { ActivityCarouselCard } from '@/components/quality-explorer/carousel/ActivityCarouselCard'
import { Activity } from '@/components/quality-explorer/types'
import { cn } from '@/utils/css'

/** A description of the available properties for this component. */
type ActivityCarouselProps = {
  /** The activities to display in the carousel. */
  activities: Array<Activity>
}

/**
 * A carousel of activities. On wide viewports this is a 3D fan: the centre activity is the largest and most
 * prominent, the others smaller and semi-transparent, navigated with the arrow buttons, the arrow keys, a swipe, or
 * by clicking a card. On narrower viewports it is a native horizontal snap list instead. Both variants are always
 * rendered and toggled purely with CSS, so the server-rendered markup never depends on the viewport.
 */
const ActivityCarousel: React.FC<ActivityCarouselProps> = ({ activities }) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [snapScroll, setSnapScroll] = useState({ atStart: true, atEnd: true })
  const pointerStartX = useRef<number | null>(null)
  const suppressClickRef = useRef(false)
  const snapListRef = useRef<HTMLUListElement>(null)
  const isSingleCard = activities.length === 1

  /** Track whether the snap list sits at either end, so the arrows can hide when there's nothing to scroll to. */
  const updateSnapScroll = () => {
    const list = snapListRef.current
    if (!list) return
    setSnapScroll({
      atStart: list.scrollLeft <= 8,
      atEnd: list.scrollLeft >= list.scrollWidth - list.clientWidth - 8,
    })
  }

  useEffect(() => {
    updateSnapScroll()
    window.addEventListener('resize', updateSnapScroll)
    return () => window.removeEventListener('resize', updateSnapScroll)
  }, [activities])

  // Start from the first card whenever the set of activities changes (e.g. a filter was applied), as the previous
  // index may point past the end of the new list.
  useEffect(() => {
    setCurrentIndex(0)
  }, [activities])

  /** Handle the click event for the previous button. */
  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + activities.length) % activities.length)
  }

  /** Handle the click event for the next button. */
  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % activities.length)
  }

  /** Scroll the snap list by one card, for mouse users who can't comfortably scroll horizontally. */
  const scrollSnapList = (direction: -1 | 1) => {
    const list = snapListRef.current
    const firstItem = list?.firstElementChild
    if (!list || !firstItem) return
    const step = firstItem.getBoundingClientRect().width + 16 // matches the list's gap-4
    list.scrollBy({
      left: direction * step,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    })
  }

  if (activities.length === 0) return null

  const focalNumber = Math.min(currentIndex + 1, activities.length)

  return (
    <div className={'w-full max-w-7xl mx-auto px-4 py-8'}>
      {/* The 1111px boundary below and on the snap list must stay in sync; it replaces the old JS media query. */}
      <div className={'hidden min-[1111px]:block'}>
        <div className={'relative'}>
          {/* Stacking every card in the same grid cell makes the container as tall as the tallest card, and every
              other card stretches to match — no measuring required, and it re-flows on resize, filtering, font load
              and the read-more toggle. */}
          <div
            role={'region'}
            aria-roledescription={'carousel'}
            aria-label={'Activities'}
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'ArrowLeft') {
                event.preventDefault()
                handlePrevious()
              } else if (event.key === 'ArrowRight') {
                event.preventDefault()
                handleNext()
              }
            }}
            onPointerDown={(event) => {
              // Swipes are for touch and pen only; a mouse drag is more likely selecting text in a card, and mouse
              // users already have the arrow buttons and keys.
              pointerStartX.current = event.pointerType === 'mouse' ? null : event.clientX
              suppressClickRef.current = false
            }}
            onPointerUp={(event) => {
              if (pointerStartX.current === null) return
              const deltaX = event.clientX - pointerStartX.current
              pointerStartX.current = null
              if (Math.abs(deltaX) > 50) {
                suppressClickRef.current = true
                if (deltaX < 0) handleNext()
                else handlePrevious()
              }
            }}
            onClickCapture={(event) => {
              // A horizontal swipe should navigate, not also trigger the click on whichever card it ended over.
              if (suppressClickRef.current) {
                suppressClickRef.current = false
                event.preventDefault()
                event.stopPropagation()
              }
            }}
            className={'relative grid touch-pan-y focus-visible:outline-2 focus-visible:outline-secondary-500'}
          >
            {activities.map((activity, index) => {
              let offset = (index - currentIndex + activities.length) % activities.length
              if (offset > activities.length / 2) offset -= activities.length

              return (
                <ActivityCarouselCard
                  key={activity.alias_p}
                  activity={activity}
                  offset={offset}
                  onClick={() => setCurrentIndex(index)}
                  mode={isSingleCard ? 1 : 2}
                  className={cn(isSingleCard ? 'w-[70%]' : 'w-[38%]')}
                />
              )
            })}
          </div>

          {activities.length > 1 && (
            <div className={'flex w-full justify-between absolute top-1/2 -translate-y-1/2'}>
              <button
                type={'button'}
                aria-label={'Previous activity'}
                onClick={handlePrevious}
                className={'p-3 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors'}
              >
                <ChevronLeft className={'w-6 h-6'} />
              </button>
              <button
                type={'button'}
                aria-label={'Next activity'}
                onClick={handleNext}
                className={'p-3 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors'}
              >
                <ChevronRight className={'w-6 h-6'} />
              </button>
            </div>
          )}
        </div>

        {activities.length > 1 && (
          <p className={'mt-6 text-center text-sm text-gray-500'} aria-live={'polite'}>
            {focalNumber} of {activities.length}
            <span className={'sr-only'}>: {activities[focalNumber - 1]?.name}</span>
          </p>
        )}
      </div>

      {/* The narrow-viewport variant: a native horizontal snap list. Native scrolling brings its own swipe,
          momentum and keyboard behaviour; the overlaid arrows exist for mouse users, for whom horizontal
          scrolling is awkward — pointer-fine hides them on touch devices, and each arrow hides at its end
          of the scroll. */}
      <div className={'relative min-[1111px]:hidden'}>
        <ul
          ref={snapListRef}
          onScroll={updateSnapScroll}
          aria-label={'Activities'}
          className={'flex gap-4 overflow-x-auto snap-x pb-4'}
        >
          {activities.map((activity) => (
            <li key={activity.alias_p} className={'snap-center shrink-0 w-[85%] sm:w-[55%] md:w-[45%]'}>
              <ActivityCardContent activity={activity} isActive={true} />
            </li>
          ))}
        </ul>
        <div
          className={
            'pointer-events-none hidden pointer-fine:flex w-full justify-between absolute top-1/2 -translate-y-1/2'
          }
        >
          <button
            type={'button'}
            aria-label={'Scroll activities backwards'}
            onClick={() => scrollSnapList(-1)}
            className={cn(
              'pointer-events-auto p-3 rounded-full bg-gray-100/90 hover:bg-gray-200 transition-colors shadow-mta',
              snapScroll.atStart && 'invisible',
            )}
          >
            <ChevronLeft className={'w-6 h-6'} />
          </button>
          <button
            type={'button'}
            aria-label={'Scroll activities forwards'}
            onClick={() => scrollSnapList(1)}
            className={cn(
              'pointer-events-auto p-3 rounded-full bg-gray-100/90 hover:bg-gray-200 transition-colors shadow-mta',
              snapScroll.atEnd && 'invisible',
            )}
          >
            <ChevronRight className={'w-6 h-6'} />
          </button>
        </div>
      </div>
    </div>
  )
}

export { ActivityCarousel }
